import type { VendorInfo, VendorMatchCriteria } from '@acitra/shared';

interface MatchCriteriaConfigProps {
  extractedVendor: VendorInfo;
  effectiveCurrency?: string;
  criteria: VendorMatchCriteria;
  onCriteriaChange: (criteria: VendorMatchCriteria) => void;
}

interface CriteriaOption {
  key: keyof VendorMatchCriteria;
  label: string;
  defaultValue: string;
}

export function MatchCriteriaConfig({
  extractedVendor,
  effectiveCurrency,
  criteria,
  onCriteriaChange,
}: MatchCriteriaConfigProps) {
  // Build available criteria options based on extracted data
  const availableOptions: CriteriaOption[] = [];

  if (extractedVendor.name) {
    availableOptions.push({
      key: 'vendorNameContains',
      label: 'Vendor name contains',
      defaultValue: extractedVendor.name,
    });
  }

  if (extractedVendor.accountNumber) {
    availableOptions.push({
      key: 'accountNumber',
      label: 'Account number is',
      defaultValue: extractedVendor.accountNumber,
    });
  }

  if (extractedVendor.email) {
    availableOptions.push({
      key: 'email',
      label: 'Email is',
      defaultValue: extractedVendor.email,
    });
  }

  if (extractedVendor.phone) {
    availableOptions.push({
      key: 'phone',
      label: 'Phone is',
      defaultValue: extractedVendor.phone,
    });
  }

  if (extractedVendor.address?.postalCode) {
    availableOptions.push({
      key: 'postalCode',
      label: 'Postal code is',
      defaultValue: extractedVendor.address.postalCode,
    });
  }

  if (effectiveCurrency) {
    availableOptions.push({
      key: 'currency',
      label: 'Currency is',
      defaultValue: effectiveCurrency,
    });
  }

  const toggleCriteria = (option: CriteriaOption) => {
    const newCriteria = { ...criteria };
    if (criteria[option.key]) {
      delete newCriteria[option.key];
    } else {
      newCriteria[option.key] = option.defaultValue;
    }
    onCriteriaChange(newCriteria);
  };

  const updateCriteriaValue = (key: keyof VendorMatchCriteria, value: string) => {
    onCriteriaChange({ ...criteria, [key]: value });
  };

  if (availableOptions.length === 0) {
    return (
      <p className="text-sm text-gray-500">No matching criteria available from extracted data.</p>
    );
  }

  const selectedCount = Object.keys(criteria).length;

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        When future invoices match{' '}
        {selectedCount === 0
          ? 'ANY'
          : selectedCount === 1
            ? 'this criterion'
            : `ALL ${selectedCount} criteria`}
        :
      </p>
      <div className="bg-white rounded-md border border-gray-200 divide-y divide-gray-200">
        {availableOptions.map((option) => {
          const isChecked = !!criteria[option.key];
          const currentValue = criteria[option.key] || option.defaultValue;

          return (
            <div key={option.key} className="px-3 py-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleCriteria(option)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
              {isChecked && (
                <div className="mt-2 ml-7">
                  <input
                    type="text"
                    value={currentValue}
                    onChange={(e) => updateCriteriaValue(option.key, e.target.value)}
                    className="w-full px-2 py-1 text-sm font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selectedCount === 0 && (
        <p className="text-xs text-amber-600">
          Select at least one criterion to create a matching rule.
        </p>
      )}
      {selectedCount > 0 && (
        <p className="text-xs text-gray-500">
          Future invoices matching{' '}
          {selectedCount === 1 ? 'this criterion' : 'all selected criteria'} will auto-suggest this
          vendor.
        </p>
      )}
    </div>
  );
}
